import React, { useState, useMemo } from 'react';
import {
  Typography,
  Box,
  Chip,
} from '@mui/material';
import AddFollowDialog from '../../dialogs/AddFollowDialog';

const BONUS_OPTION_TIMING_RATE = 'timing_rate';
const BET_CLOSE_MODE = {
  TIMER: 'timer',
  MANUAL: 'manual',
};
const RESULT_CONFIRMATION_METHOD_MEMBER = 'member_confirmation';

const formatDuration = (seconds) => {
  const totalSeconds = Math.max(0, Math.floor(Number(seconds) || 0));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const remainingSeconds = totalSeconds % 60;

  return [
    hours > 0 ? `${hours}時間` : '',
    minutes > 0 ? `${minutes}分` : '',
    remainingSeconds > 0 ? `${remainingSeconds}秒` : '',
  ].filter(Boolean).join('') || '0秒';
};

const hasTimingRate = (bonusOptions) => {
  if (Array.isArray(bonusOptions)) {
    return bonusOptions.includes(BONUS_OPTION_TIMING_RATE);
  }

  if (typeof bonusOptions === 'string') {
    try {
      const parsed = JSON.parse(bonusOptions);
      return Array.isArray(parsed) && parsed.includes(BONUS_OPTION_TIMING_RATE);
    } catch (e) {
      return false;
    }
  }

  return false;
};

const formatBetRange = (minimumBet, maxBet) => {
  const minimum = Number(minimumBet);
  const maximum = Number(maxBet);
  const minimumLabel = Number.isFinite(minimum) ? minimum.toLocaleString() : minimumBet;
  const maximumLabel = Number.isFinite(maximum) ? maximum.toLocaleString() : maxBet;

  return `${minimumLabel}〜${maximumLabel} pt`;
};

function RoomDetail({ event, user, masterTags, updateMasterTags }) {
  const [addFollowDialogVisible, setAddFollowDialogVisible] = useState(false);
  const [follow, setFollow] = useState(JSON.parse(user.follow));

  const isFollowed = (id) => follow.includes(id);
  const addFollow = (id) => setFollow([...follow, id]);
  const removeFollow = (id) => setFollow(follow.filter(item => item !== id));

  const dialogDatas = { id: event.owner_id };

  const tagLabels = useMemo(() => {
    if (event.tags && masterTags) {
      try {
        const tagIds = event.tags;
        if (!Array.isArray(tagIds)) return [];

        const labels = tagIds.map(id => {
          const match = masterTags.find(tag => tag.id === id);
          return match ? match.name : null;
        });

        if (labels.includes(null)) {
          console.warn('不明なタグIDが含まれているためマスタ再取得を要求');
          if (updateMasterTags) updateMasterTags();
          return [];
        }

        return labels;
      } catch (e) {
        console.error('タグの解析中にエラー', e);
        return [];
      }
    }
    return [];
  }, [event.tags, masterTags, updateMasterTags]);

  const formatElapsedTime = (createdAt) => {
    if (!createdAt) return '不明';
    const created = new Date(createdAt);
    const now = new Date();
    const diffMs = now - created;
    const diffH = Math.floor(diffMs / 1000 / 60 / 60);
    const diffM = Math.floor((diffMs / 1000 / 60) % 60);
    if (diffH === 0 && diffM === 0) return 'たった今';
    if (diffH === 0) return `${diffM}分前`;
    if (diffM === 0) return `${diffH}時間前`;
    return `${diffH}時間${diffM}分前`;
  };

  const isTimerClose = event.bet_close_mode === BET_CLOSE_MODE.TIMER;
  const isManualClose = event.bet_close_mode === BET_CLOSE_MODE.MANUAL;
  const rateConfirmationLabel = hasTimingRate(event.bonus_options)
    ? 'ベット時に確定'
    : '受付終了時に確定';
  const betCloseLabel = isTimerClose
    ? `${formatDuration(event.bet_time)}後に自動終了`
    : 'オーナーが操作';
  const resultConfirmationLabel =
    event.result_confirmation_method === RESULT_CONFIRMATION_METHOD_MEMBER
      ? '参加者による確認あり'
      : '参加者による確認なし';

  const roomRules = [
    { label: 'レート確定', value: rateConfirmationLabel },
    { label: '受付終了', value: betCloseLabel },
    ...(isManualClose
      ? [{ label: 'レート変動の基準時間', value: formatDuration(event.bet_time) }]
      : []),
    { label: '結果確認', value: resultConfirmationLabel },
    { label: 'ベット範囲', value: formatBetRange(event.minimum_bet, event.max_bet) },
  ];

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: { xs: '720px', md: 'none' },
        mx: 'auto',
        mt: 1,
        backgroundColor: '#f5f7fa',
        borderRadius: '8px',
        border: '1px solid #ddd',
        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
        padding: '12px 16px',
        boxSizing: 'border-box',
      }}
    >
      <AddFollowDialog
        isVisible={addFollowDialogVisible}
        onClose={() => setAddFollowDialogVisible(false)}
        dialogDatas={dialogDatas}
        isFollowed={isFollowed}
        addFollow={addFollow}
        removeFollow={removeFollow}
      />
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
        ルーム詳細情報
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) minmax(220px, 1fr)' },
          gap: { xs: '12px', md: '12px 24px' },
          mb: 1,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Box sx={{ fontSize: '12px', color: '#666', mr: 1 }}>
              作成者: {event.owner_name}
            </Box>
            <Box
              onClick={() => setAddFollowDialogVisible(true)}
              sx={{
                fontSize: '12px',
                color: '#1976d2',
                border: '1px solid #1976d2',
                borderRadius: '4px',
                padding: '2px 6px',
                backgroundColor: 'white',
                cursor: 'pointer',
                userSelect: 'none',
                flexShrink: 0,
                '&:hover': {
                  backgroundColor: '#e3f2fd',
                },
              }}
            >
              {isFollowed(event.owner_id) ? 'フォローから外す' : 'フォローに追加'}
            </Box>
          </Box>

          <Box sx={{ fontSize: '12px', color: '#666', mb: 1 }}>
            ルーム作成: {formatElapsedTime(event.created_at)}
          </Box>

          <Box sx={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
            {tagLabels.map((label, idx) => (
              <Chip key={idx} label={label} size="small" color="primary" />
            ))}
          </Box>
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" fontWeight="bold" sx={{ color: '#555', display: 'block', mb: 0.5 }}>
            ルームルール
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'max-content minmax(0, 1fr)',
              columnGap: 1.5,
              rowGap: '4px',
              fontSize: '12px',
              color: '#555',
            }}
          >
            {roomRules.map((rule) => (
              <React.Fragment key={rule.label}>
                <Box component="span" sx={{ color: '#777' }}>{rule.label}</Box>
                <Box component="span" sx={{ fontWeight: 500 }}>{rule.value}</Box>
              </React.Fragment>
            ))}
          </Box>
        </Box>
      </Box>

      <Box sx={{ fontSize: '14px', color: '#555' }}>{event.description}</Box>
    </Box>
  );
}

export default RoomDetail;
